import { useEffect, useRef } from "react";
import { useSettingsStore } from "../store/settingsStore";

/**
 * Manages Screen Wake Lock API based on keepScreenAwake setting.
 * Acquires lock when enabled + document visible, releases when disabled.
 * Re-acquires automatically on visibility change (tab switch back).
 */
export function useWakeLock() {
  const keepScreenAwake = useSettingsStore((state) => state.keepScreenAwake);
  const wakeLockRef = useRef(null);

  useEffect(() => {
    if (!("wakeLock" in navigator)) return;

    const acquire = async () => {
      if (document.hidden) return;
      if (wakeLockRef.current && !wakeLockRef.current.released) return;

      try {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
        console.log("[WakeLock] Acquired");

        wakeLockRef.current.addEventListener("release", () => {
          console.log("[WakeLock] Released");
          wakeLockRef.current = null;
        });
      } catch (err) {
        console.warn(`[WakeLock] Failed: ${err.name}`);
      }
    };

    const release = async () => {
      if (wakeLockRef.current && !wakeLockRef.current.released) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && keepScreenAwake) {
        acquire();
      }
    };

    if (keepScreenAwake) {
      acquire();
      document.addEventListener("visibilitychange", handleVisibilityChange);
    } else {
      release();
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      release();
    };
  }, [keepScreenAwake]);
}
